import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Descendant, Editor, Range, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import { appNote } from '../constants';
import { computeLength, IMentionProps, mapDescendantsToText, mapTextToDescendants, MentionElement } from './models';
import { Portal } from './portal.component';
import { UtilityService } from '../../../services/UtilityService';
import { CancelIcon } from '../../../icon';

const MentionComponent = (props: IMentionProps) => {
    const ref = useRef<any>()
    const [value, setValue] = useState<Descendant[]>([])
    const [target, setTarget] = useState<Range | undefined | null>()
    const [index, setIndex] = useState(-1)
    const [top, setTop] = useState(-9999)
    const [left, setLeft] = useState(-9999)
    const [search, setSearch] = useState('')
    const [openTags, setOpenTags] = useState(false);
    const [showingCategoryIndex, setShowingCategoryIndex] = useState<number>(0);
    const [isTouched, setIsTouched] = useState(false);
    const renderElement = useCallback(innerProps => <Element {...innerProps} tags={props.tags} IsOutbound={props.IsOutbound} spellCheck={true} />, [])
    const editor = useMemo(
        () => withMentions(withReact(withHistory(createEditor() as any))),
        []
    )
    const chars = search ? props.tags.map(x => ({
        ...x, tags: x.tags.filter(c =>
            c.id.toLowerCase().startsWith(search.toLowerCase())
        ).slice(0, 10)
    })).filter(x => x.tags.length) : props.tags.slice(0, 10)

    const allTags = props.tags.reduce((p, c) => [...p, ...c.tags], [])
    const [IsGroupChat, setIsGroupChat] = useState<boolean>(false)

    useEffect(() => {
        setIsGroupChat(props.IsGroupChat)
    }, [props.IsGroupChat])

    const onKeyDown = useCallback(
        event => {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    const prevIndex = index >= chars.length - 1 ? 0 : index + 1;
                    setIndex(prevIndex);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    const nextIndex = index <= 0 ? chars.length - 1 : index - 1;
                    setIndex(nextIndex);
                    break;
                case 'Tab':
                case 'Enter':
                    if ((event.altKey || event.metaKey || event.shiftKey)) {
                        event.preventDefault();
                        Transforms.insertText(editor, '\n');
                    }
                    if (target) {
                        if (index >= 0 && chars[index]) {
                            event.preventDefault()
                            Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                            insertMention(editor, chars[showingCategoryIndex].tags[index]?.display, chars[showingCategoryIndex].tags?.length)
                        }
                        setOpenTags(false)
                        setSearch("")
                        setTarget(null)
                        setIndex(-1)
                    }
                    else if (openTags) {
                        if (!props.openTags && index >= 0 && chars[index]) {
                            event.preventDefault()
                            Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                        }
                        if (index >= 0 && chars[index]) {
                            insertMention(editor, chars[showingCategoryIndex].tags[index]?.display, chars[showingCategoryIndex].tags[index]?.length)
                        }
                        setOpenTags(false)
                        setSearch("")
                        setTarget(null)
                        setIndex(-1)
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    setOpenTags(false);
                    props.closeTags();
                    setTarget(null)
                    break;
                case 'Backspace':
                    setOpenTags(false)
                    break;
                case '@':
                    if (IsGroupChat) {
                        setOpenTags(false)
                        setSearch("")
                        setTarget(null)
                        setIndex(-1)
                    } else {
                        const { selection } = editor
                        const [start] = Range.edges(selection)
                        const wordBefore = Editor.before(editor, start, { unit: 'word' })
                        const before = wordBefore && Editor.before(editor, wordBefore)
                        const beforeRange = before && Editor.range(editor, before, start)
                        setTarget(beforeRange || Editor.range(editor, { offset: index - 1, path: [0, 0] }, { offset: index, path: [0, 0] }));
                        setSearch("")
                        setOpenTags(true)
                    }
                    break;
            }
            if (/[`~"':;/?.<>,|\\!#$%^&*()_+=-\s]/.test(event.key)) {
                setOpenTags(false)
                setSearch("")
                setTarget(null)
                setIndex(-1)
            }
            props.onKeyDown(event)
        },
        [index, search, target]
    )
    useEffect(() => {
        const el = ref.current;
        if (IsGroupChat) {
            setOpenTags(false)
            setSearch("")
            setTarget(null)
            setIndex(-1)
        }
        else if (target && chars.length > 0 && el) {
            const domRange = ReactEditor.toDOMRange(editor, target)
            const rect = domRange.getBoundingClientRect()
            el.style.top = (window.innerHeight < rect.bottom + el.offsetHeight) ? `${rect.top - el.offsetHeight}px` : `${rect.top + window.pageYOffset + 24}px`
            el.style.left = (window.innerWidth < rect.right + el.offsetWidth) ? `${rect.right - el.offsetWidth + ((el.offsetWidth / 2))}px` : `${rect.left + rect.width + window.pageXOffset}px`
        }
        else {
            if (props.tagTarget && props.openTags) {
                if (props.IsMobileDevice && props.IsNewMessage) {
                    setSearch("")
                    setTarget(null)
                    let top = props.tagTarget.offsetTop + ((props.tagTarget.offsetParent as any)?.offsetTop || 0) + window.pageYOffset - 150 + 200;
                    top = top + (131 * (chars.length / props.tags.length)) > window.innerHeight ? window.innerHeight - 131 - 48 : top;
                    setTop(top);
                    setLeft(props.tagTarget.offsetLeft + ((props.tagTarget.offsetParent as any)?.offsetLeft || 0) + window.pageXOffset - 60);
                    setOpenTags(props.openTags)
                }
                else if (props.IsMobileDevice) {
                    setSearch("")
                    setTarget(null)
                    let top = props.tagTarget.offsetTop + ((props.tagTarget.offsetParent as any)?.offsetTop || 0) + window.pageYOffset - 150;
                    top = top + (131 * (chars.length / props.tags.length)) > props.deviceHeight ? props.deviceHeight - 131 - 48 : top;
                    setTop(top);
                    setLeft(props.tagTarget.offsetLeft + ((props.tagTarget.offsetParent as any)?.offsetLeft || 0) + window.pageXOffset - 60);
                    setOpenTags(props.openTags)
                }
                else {
                    setSearch("")
                    setTarget(null)
                    setOpenTags(props.openTags)
                    let top = props.tagTarget.offsetTop + ((props.tagTarget.offsetParent as any)?.offsetTop || 0) + window.pageYOffset - 150;
                    top = top + (131 * (chars.length / props.tags.length)) > window.innerHeight ? window.innerHeight - 131 - 48 : top;
                    setTop(top);
                    setLeft(props.tagTarget.offsetLeft + ((props.tagTarget.offsetParent as any)?.offsetLeft || 0) + window.pageXOffset - 90);
                }
            }
        }
        if (chars.length <= showingCategoryIndex) {
            setShowingCategoryIndex(0)
        }
    }, [chars.length, editor, index, search, target, props.openTags, props.tagTarget])

    useEffect(() => {
        if (props.emoji) {
            Editor.insertText(editor, props.emoji)
        }
    }, [props.emoji])

    useEffect(() => {
        if (props.Focus) {
            setTimeout(() => {
                ReactEditor.focus(editor);
            }, 1)
        }
    }, [props.Focus])


    useEffect(() => {
        if (!props.body) {
            clearEditor();
            editor.children = [];
            Transforms.insertNodes(editor, [{ type: "paragraph", children: [{ text: "" }] } as any])
        }
        else {
            let des = mapTextToDescendants(props.body, allTags, props.IsLinkAllowed);
            let text = mapDescendantsToText(value);

            if (text !== props.body) {
                clearEditor();
                editor.children = [];
                Transforms.insertNodes(editor, des)
            }
        }
    }, [props.body]);

    const clearEditor = () => {
        const editor_selection = editor.selection;
        if (!editor_selection) {
            return;
        }

        const [, first_path] = Editor.first(editor, [0]);
        const [, last_path] = Editor.last(editor, [
            editor.children.length - 1,
        ]);

        const full_range = Editor.range(
            editor,
            first_path,
            last_path
        );

        full_range.focus.offset = full_range.focus.offset === 0 ? 1 : full_range.focus.offset;
        Transforms.setSelection(editor, full_range)
        const is_everything_selected = Range.equals(editor.selection, full_range);

        if (is_everything_selected) {
            Transforms.removeNodes(editor);
            return;
        }
    }

    const handleOutsideClick = (event: any) => {
        event?.stopPropagation()
        setOpenTags((ref.current?.contains(event.target) || ref.current === event.target || (`${event.target.id}-mentions` === props.id && !props.openTags)))
        props.closeTags()
    }

    const handleKeyDown = (event: any) => {
        event?.stopPropagation();
        if (event.key === "Escape") {
            setOpenTags(false)
        }
    }

    useEffect(() => {
        setOpenTags(props.openTags)
    }, [props.openTags])


    useEffect(() => {
        const handleClickOutside = (e) => {
            handleOutsideClick(e);
        };

        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleOutsideClick]);

    // useEffect(()=>{
    //     setIsTouched(false)
    // }, [props.phoneNumbers])

    const getDisabledTags = (char: any) => {
        if (char.className === "user") {
            if (char.id === "MyFirstName" || char.id === "MyLastName" || char.id === "MyEmail") {
                return "disabled"
            } else {
                return char.className
            }
        } else {
            return char.className
        }
    }


    return (
        <Slate
            editor={editor}
            initialValue={value}
            onChange={value => {
                setValue(value)
                let lastChar: string;
                const { selection } = editor
                if (selection && Range.isCollapsed(selection)) {
                    const [start] = Range.edges(selection)
                    const wordBefore = Editor.before(editor, start, { unit: 'word' })
                    const before = wordBefore && Editor.before(editor, wordBefore)
                    const beforeRange = before && Editor.range(editor, before, start)
                    const beforeText = beforeRange && Editor.string(editor, beforeRange)
                    const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/)
                    const after = Editor.after(editor, start)
                    const afterRange = Editor.range(editor, start, after)
                    const afterText = Editor.string(editor, afterRange)
                    const afterMatch = afterText.match(/^(\s|$)/)

                    const charBefore = Editor.before(editor, start, { unit: 'character' })
                    const beforeChar = charBefore && Editor.before(editor, charBefore)
                    const beforeCharRange = beforeChar && Editor.range(editor, beforeChar, start)
                    lastChar = beforeCharRange && Editor.string(editor, beforeCharRange)

                    if (beforeRange && beforeMatch) {
                        setTarget(beforeRange)
                        setSearch(beforeMatch[1])
                        setIndex(-1)
                        return
                    }
                }
                setTarget(null);
                props.closeTags();
                if (isTouched) {
                    props.onChange({
                        target: {
                            value: mapDescendantsToText(value) || ""
                        }
                    }, computeLength(value))
                }
                setIsTouched(true)

                if (!IsGroupChat && UtilityService.isAndroid(navigator.userAgent) && (mapDescendantsToText(value).slice(-1) === "@"
                    || lastChar?.slice(-1) === "@")) {
                    const { selection } = editor
                    const [start] = Range.edges(selection)
                    const wordBefore = Editor.before(editor, start, { unit: 'word' })
                    const before = wordBefore && Editor.before(editor, wordBefore)
                    const beforeRange = before && Editor.range(editor, before, start)
                    setTarget(beforeRange || Editor.range(editor, { offset: index - 1, path: [0, 0] }, { offset: index, path: [0, 0] }));
                    setSearch("")
                    setOpenTags(true)
                } else if (UtilityService.isAndroid(navigator.userAgent)) {
                    setOpenTags(false)
                    props.closeTags();
                }
            }}

        >
            {!!props.FacebookTemplate ?
                <div className=' d-flex justify-between align-center bg-color-template-background p-1'>
                    <div className={`d-flex h5-regular  w-p-50`}>{props?.FacebookTemplate?.Title}</div>
                    <CancelIcon
                        className={"c-pointer"}
                        onClick={() => { props.RemoveFacebookTemplate() }}
                    />
                </div> :
                <Editable
                    renderElement={(params) => renderElement({ ...params, index: showingCategoryIndex })}
                    onKeyDown={onKeyDown}
                    style={{ lineHeight: props.lineHeight ? props.lineHeight : 1.5, overflowWrap: "anywhere" }}
                    placeholder={props.placeholder}
                    maxLength={props.maxLength}
                    // autoFocus={props.IsMobileDevice}
                    tabIndex={11}
                    readOnly={!!props.readOnly}
                    onBlur={props.onBlur}
                    spellCheck={true}
                />
            }
            {
                ((target && chars.some(x => x.tags.length)) || ((openTags) && chars.some(x => x.tags.length))) && (
                    <Portal>
                        <div
                            ref={ref}
                            style={{
                                top: top,
                                left: left,
                                position: 'absolute',
                                zIndex: 2000,
                                background: 'white',
                                boxShadow: '0 1px 5px rgba(0,0,0,.2)',
                                borderRadius: 6,
                            }}
                            className={`h4-regular tags-max-width ${props.IsNewMessage ? "tags-newMessage" : "tags"} ph-no-capture`}
                            data-cy="mentions-portal"
                        >
                            <div className='color-tags-title h4-regular px-1 pt-1'>{appNote.CUSTOM_TAGS}</div>

                            <div className='d-flex justify-around'>
                                {
                                    chars.map((char, i) => (
                                        <div key={i}>
                                            <div className=''>
                                                <div className={`px-1 pt-1 c-pointer  h5-regular ${showingCategoryIndex === i ? "color-tags-title" : "color-tags-title-unselect"} `} onClick={(e) => { e.stopPropagation(); setShowingCategoryIndex(i); }}>{char.label} </div>
                                            </div>
                                        </div>))
                                }
                            </div>
                            {!search && <div className="d-flex mx-1">
                                <div className={`show-conversation-animation  show-conversation-border show-conversation-height bg-color-primary-light ${showingCategoryIndex === 0 ? "ml-p-0 w-p-53" : "ml-p-62 w-p-36"}  `}></div>
                            </div>}
                            <div className='bb-primary-divider-2'></div>

                            {<div className={"d-flex flex-wrap footnote justify-between p-1 h-9"}>   {chars[showingCategoryIndex]?.tags?.map((char, i) => (
                                <div
                                    key={char.id}
                                    className={`c-pointer tag my-auto ${props.IsAddRule ? getDisabledTags(char) : char.className}`}
                                    onClick={(event: any) => {
                                        event.preventDefault(); event.stopPropagation();
                                        if (target) {
                                            Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                                            insertMention(editor, chars[showingCategoryIndex].tags[i].id, chars[showingCategoryIndex].tags[i].length)
                                            setOpenTags(false)
                                            setSearch("")
                                            setTarget(null)
                                            setIndex(-1)
                                        }
                                        else if (openTags) {
                                            if (!props.openTags) {
                                                if (UtilityService.isAndroid(navigator.userAgent) && mapDescendantsToText(value).slice(-1) === "@") {
                                                    let bodyText = mapDescendantsToText(value).slice(0, -1);
                                                    let desc = mapTextToDescendants(bodyText, allTags, props.IsLinkAllowed);
                                                    clearEditor();
                                                    editor.children = [];
                                                    Transforms.insertNodes(editor, desc)
                                                }
                                                else {
                                                    Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                                                }
                                            }
                                            insertMention(editor, chars[showingCategoryIndex].tags[i].id, chars[showingCategoryIndex].tags[i].length)
                                            setOpenTags(false)
                                            setSearch("")
                                            setTarget(null)
                                            setIndex(-1)
                                        }
                                    }}
                                >
                                    {chars[showingCategoryIndex].tags[i].display}
                                </div>
                            ))}
                            </div>}
                        </div>
                    </Portal>
                )}
        </Slate>
    )
}

const withMentions = (editor: any) => {
    const { isInline, isVoid } = editor

    editor.isInline = (element: any) => {
        return element.type === 'mention' || element.type === 'link' || element.type === 'mail' ? true : isInline(element)
    }

    editor.isVoid = (element: any) => {
        return element.type === 'mention' || element.type === 'link' || element.type === 'mail' ? true : isVoid(element)
    }

    return editor
}

const insertMention = (editor: any, character: any, length: number) => {
    const mention: MentionElement = {
        type: 'mention',
        character,
        length,
        children: [{ text: '' }],
        className: ""
    }
    Transforms.insertNodes(editor, mention as any)
    Transforms.move(editor)
}

const Element = (props: any) => {
    const { attributes, children, element, tags, IsOutbound, spellCheck } = props;
    switch (element.type) {
        case 'mention':
            return <Mention {...props} tags={tags} />
        case 'link':
            let regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm
            let url = element.character.match(regex) ? element.character : `http://${element.character}`
            return <a
                style={{ cursor: "pointer" }}
                href={url}
                className={`${IsOutbound !== undefined && IsOutbound ? "color-text-field-focused t-decoration-underline" : "conversation-link"}`}
                target="_blank" rel="noopener noreferrer"
            >
                {element.character}
            </a>
        case 'mail':
            return <a
                style={{ cursor: "pointer" }}
                href={`mailto: ${element.character}`}
                className={IsOutbound !== undefined && IsOutbound ? "color-text-field-focused t-decoration-underline" : "conversation-link"}
                target="_blank" rel="noopener noreferrer"
            >
                {element.character}
            </a>
        default:
            return <p className='my-auto' {...attributes} spellCheck={spellCheck}>{children}</p>
    }
}

const Mention = ({ attributes, children, element, tags, IsOutbound }: any) => {

    const allTags = tags.reduce((p, c) => [...p, ...c.tags], [])
    return (
        <span
            {...attributes}
            contentEditable={false}
            data-cy={`mention-${element.character.replace(' ', '-')}`}
            className={`tag ${(allTags.find((x: any) => x.id === element.character.replaceAll(" ", ""))?.className === "contact" && IsOutbound) ? "bg-color-secondary" : allTags.find((x: any) => x.id === element.character.replaceAll(" ", ""))?.className}`}
        >
            {allTags.find((x: any) => x.id === element.character)?.display || element.character}
            {children}
        </span >
    )
}

export default MentionComponent